import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // === MAIN GAME ANIMATION STATES ===
  counterAnimationStarted: false, // Tracks if the counter animation is started
  counterAnimationFinished: false, // Tracks if the counter animation is finished
  skipCounterAnimation: false, // Tracks if user wants to skip counter animation immediately
  linesRotationFinished: false, // Tracks if the lines rotation is finished
  currentWinningLine: null, // Tracks the current winning line
  winAnimationsPlaying: false, // Tracks if win animations (spine + counter) are playing
  isAnimatedWin: false, // Tracks if current win qualifies for Big Win animation (set immediately on spin result)
  criticalAnimationPlaying: false, // Universal blocker - blocks user input during critical animations (expanding wilds, intro sequences, etc.)
  userWantsToDismissWinAnimation: false, // Tracks if user wants to dismiss win animation
  skipWinAnimation: false, // Tracks if user wants to skip win animation immediately
  isWaitingForUserConfirmation: false, // Tracks if animation is waiting for user to dismiss
  animatedWinAmount: 0, // Current animated win amount value (persists across screen resizes)
  resetMainGameVisualEffects: false, // Trigger to reset main game visual effects and animations
  introTransitionActive: false, // Tracks if intro popup transition is active
  showGameAfterTransition: false, // Tracks if game should be shown after transition completes
  errorRecoveryTransitionActive: false, // Tracks if error recovery transition is active (restores reels after freeze)
  errorRecoveryScreenCovered: false, // Tracks if screen is fully covered during error recovery (safe to show UI)
  showHoldAndWinIntro: false, // Tracks if hold and win intro dialog should be shown
  sceneTransitionActive: false, // Tracks if scene transition overlay is active
  showHoldAndWinOutro: false, // Tracks if hold and win outro dialog should be shown
  showHoldAndWinCollector: false, // Tracks if hold and win collector dialog should be shown
  showMaxWin: false, // Tracks if max win dialog should be shown
  expandingWildsOccurred: false, // Tracks if expanding wilds occurred in the current spin
  holdAndWinOutroCounterFinished: false, // Tracks if H&W outro counter animation has completed
  winAnimationCounterFinished: false, // Tracks if Big/Mega/SuperMega win animation counter has completed

  // === FREE SPIN TRANSITION STATES ===
  freeSpinTransitionActive: false, // Tracks if free spin scene transition animation is active
  freeSpinTransitionMidpoint: false, // Fires when transition reaches midpoint (triggers decoration)
  showFreeSpinIntro: false, // Tracks if free spin intro dialog should be shown
  freeSpinCounterVisible: false, // Tracks if free spin counter spine animation should be visible
  freeSpinCycleComplete: false, // Tracks if all win lines have completed 1 full cycle in free spins
  freeSpinCoverStarted: false, // True when first symbol gets covered (triggers counter update)
  showFreeSpinOutro: false, // Tracks if free spin outro dialog should be shown
  showFreeSpinRetrigger: false, // Tracks if free spin retrigger dialog should be shown
};

const animationsSlice = createSlice({
  name: "animations",
  initialState,
  reducers: {
    // === MAIN GAME ANIMATION REDUCERS ===
    setCounterAnimationStarted: (state, action) => {
      state.counterAnimationStarted = action.payload;
    },

    setCounterAnimationFinished: (state, action) => {
      state.counterAnimationFinished = action.payload;
    },

    setSkipCounterAnimation: (state, action) => {
      state.skipCounterAnimation = action.payload;
    },

    setCurrentWinningLine: (state, action) => {
      state.currentWinningLine = action.payload;
    },

    setWinAnimationsPlaying: (state, action) => {
      state.winAnimationsPlaying = action.payload;
    },

    setIsAnimatedWin: (state, action) => {
      state.isAnimatedWin = action.payload;
    },

    setCriticalAnimationPlaying: (state, action) => {
      state.criticalAnimationPlaying = action.payload;
    },

    setUserWantsToDismissWinAnimation: (state, action) => {
      state.userWantsToDismissWinAnimation = action.payload;
    },

    setSkipWinAnimation: (state, action) => {
      state.skipWinAnimation = action.payload;
    },

    setIsWaitingForUserConfirmation: (state, action) => {
      state.isWaitingForUserConfirmation = action.payload;
    },

    // Update animated win amount value (for persistence across screen resizes)
    updateAnimatedWinAmount: (state, action) => {
      state.animatedWinAmount = action.payload;
    },

    // Reset main game visual effects and animations (triggered during free spin transition)
    setResetMainGameVisualEffects: (state, action) => {
      state.resetMainGameVisualEffects = action.payload;
    },

    // Control intro transition state
    setIntroTransitionActive: (state, action) => {
      state.introTransitionActive = action.payload;
    },

    // Control whether game should be shown after transition
    setShowGameAfterTransition: (state, action) => {
      state.showGameAfterTransition = action.payload;
    },

    // Control error recovery transition state
    setErrorRecoveryTransitionActive: (state, action) => {
      state.errorRecoveryTransitionActive = action.payload;
    },

    // Control error recovery screen covered state
    setErrorRecoveryScreenCovered: (state, action) => {
      state.errorRecoveryScreenCovered = action.payload;
    },

    // Control hold and win intro dialog visibility
    setShowHoldAndWinIntro: (state, action) => {
      state.showHoldAndWinIntro = action.payload;
    },

    // Control scene transition overlay visibility
    setSceneTransitionActive: (state, action) => {
      state.sceneTransitionActive = action.payload;
    },

    // Control hold and win outro dialog visibility
    setShowHoldAndWinOutro: (state, action) => {
      state.showHoldAndWinOutro = action.payload;
    },

    // Control hold and win collector dialog visibility
    setShowHoldAndWinCollector: (state, action) => {
      state.showHoldAndWinCollector = action.payload;
    },

    // Control max win dialog visibility
    setShowMaxWin: (state, action) => {
      state.showMaxWin = action.payload;
    },

    // Track if expanding wilds occurred in current spin
    setExpandingWildsOccurred: (state, action) => {
      state.expandingWildsOccurred = action.payload;
    },

    // Track if H&W outro counter animation has completed
    setHoldAndWinOutroCounterFinished: (state, action) => {
      state.holdAndWinOutroCounterFinished = action.payload;
    },

    // Track if win celebration (Big/Mega/SuperMega) counter animation has completed
    setWinAnimationCounterFinished: (state, action) => {
      state.winAnimationCounterFinished = action.payload;
    },

    // === FREE SPIN TRANSITION REDUCERS ===
    setFreeSpinTransitionActive: (state, action) => {
      state.freeSpinTransitionActive = action.payload;
    },

    // Signal that transition reached its midpoint (triggers decoration animation)
    setFreeSpinTransitionMidpoint: (state, action) => {
      state.freeSpinTransitionMidpoint = action.payload;
    },

    // Control free spin intro dialog visibility
    setShowFreeSpinIntro: (state, action) => {
      state.showFreeSpinIntro = action.payload;
    },

    // Control free spin counter visibility
    setFreeSpinCounterVisible: (state, action) => {
      state.freeSpinCounterVisible = action.payload;
    },

    // Tracks if all win lines completed 1 full cycle in free spins
    setFreeSpinCycleComplete: (state, action) => {
      state.freeSpinCycleComplete = action.payload;
    },
    setFreeSpinCoverStarted: (state, action) => {
      state.freeSpinCoverStarted = action.payload;
    },
    // Control free spin outro dialog visibility
    setShowFreeSpinOutro: (state, action) => {
      state.showFreeSpinOutro = action.payload;
    },
    // Control free spin retrigger dialog visibility
    setShowFreeSpinRetrigger: (state, action) => {
      state.showFreeSpinRetrigger = action.payload;
    },
  },
});

export const selectIntroTransitionActive = (state) =>
  state.animations.introTransitionActive;

export const selectShowGameAfterTransition = (state) =>
  state.animations.showGameAfterTransition;

export const selectResetMainGameVisualEffects = (state) =>
  state.animations.resetMainGameVisualEffects;

export const selectCounterAnimationFinished = (state) =>
  state.animations.counterAnimationFinished;

export const selectLinesRotationFinished = (state) =>
  state.animations.linesRotationFinished;

export const selectWinAnimationsPlaying = (state) =>
  state.animations.winAnimationsPlaying;

export const selectIsAnimatedWin = (state) => state.animations.isAnimatedWin;

export const selectCriticalAnimationPlaying = (state) =>
  state.animations.criticalAnimationPlaying;

export const selectUserWantsToDismissWinAnimation = (state) =>
  state.animations.userWantsToDismissWinAnimation;

export const selectSkipWinAnimation = (state) =>
  state.animations.skipWinAnimation;

export const selectIsWaitingForUserConfirmation = (state) =>
  state.animations.isWaitingForUserConfirmation;

export const selectAnimatedWinAmount = (state) =>
  state.animations.animatedWinAmount;

export const selectCounterAnimationStarted = (state) =>
  state.animations.counterAnimationStarted;

export const selectSkipCounterAnimation = (state) =>
  state.animations.skipCounterAnimation;

export const selectShowHoldAndWinIntro = (state) =>
  state.animations.showHoldAndWinIntro;

export const selectSceneTransitionActive = (state) =>
  state.animations.sceneTransitionActive;

export const selectShowHoldAndWinOutro = (state) =>
  state.animations.showHoldAndWinOutro;

export const selectShowHoldAndWinCollector = (state) =>
  state.animations.showHoldAndWinCollector;

export const selectShowMaxWin = (state) => state.animations.showMaxWin;

export const selectExpandingWildsOccurred = (state) =>
  state.animations.expandingWildsOccurred;

export const selectHoldAndWinOutroCounterFinished = (state) =>
  state.animations.holdAndWinOutroCounterFinished;

export const selectWinAnimationCounterFinished = (state) =>
  state.animations.winAnimationCounterFinished;

export const selectErrorRecoveryTransitionActive = (state) =>
  state.animations.errorRecoveryTransitionActive;

export const selectErrorRecoveryScreenCovered = (state) =>
  state.animations.errorRecoveryScreenCovered;

export const selectFreeSpinTransitionActive = (state) =>
  state.animations.freeSpinTransitionActive;

export const selectFreeSpinTransitionMidpoint = (state) =>
  state.animations.freeSpinTransitionMidpoint;

export const selectShowFreeSpinIntro = (state) =>
  state.animations.showFreeSpinIntro;

export const selectFreeSpinCounterVisible = (state) =>
  state.animations.freeSpinCounterVisible;

export const selectFreeSpinCycleComplete = (state) =>
  state.animations.freeSpinCycleComplete;

export const {
  setCounterAnimationStarted,
  setCounterAnimationFinished,
  setSkipCounterAnimation,
  setCurrentWinningLine,
  setWinAnimationsPlaying,
  setIsAnimatedWin,
  setCriticalAnimationPlaying,
  setUserWantsToDismissWinAnimation,
  setSkipWinAnimation,
  setIsWaitingForUserConfirmation,
  updateAnimatedWinAmount,
  setResetMainGameVisualEffects,
  setIntroTransitionActive,
  setShowGameAfterTransition,
  setErrorRecoveryTransitionActive,
  setErrorRecoveryScreenCovered,
  setShowHoldAndWinIntro,
  setSceneTransitionActive,
  setShowHoldAndWinOutro,
  setShowHoldAndWinCollector,
  setShowMaxWin,
  setExpandingWildsOccurred,
  setHoldAndWinOutroCounterFinished,
  setWinAnimationCounterFinished,
  setFreeSpinTransitionActive,
  setFreeSpinTransitionMidpoint,
  setShowFreeSpinIntro,
  setFreeSpinCounterVisible,
  setFreeSpinCycleComplete,
  setFreeSpinCoverStarted,
  setShowFreeSpinOutro,
  setShowFreeSpinRetrigger,
} = animationsSlice.actions;

export const selectFreeSpinCoverStarted = (state) =>
  state.animations.freeSpinCoverStarted;

export const selectShowFreeSpinOutro = (state) =>
  state.animations.showFreeSpinOutro;

export const selectShowFreeSpinRetrigger = (state) =>
  state.animations.showFreeSpinRetrigger;

export default animationsSlice.reducer;
